import type {
  FooterConfig,
  FooterPlatform,
  FooterMode,
  LabelAfterPlacement,
  LabelBeforePlacement,
} from "./types";
import {
  DEFAULT_ASSETS_BASE,
  DEFAULT_CDN_BASE,
  DEFAULT_CLIENTS_BASE,
  DEFAULT_CONFIG,
  DEFAULT_LABEL_AFTER,
  DEFAULT_LINK,
  FOOTER_MODES,
  PLATFORM_PRESETS,
  type FooterAssets,
} from "./types";
import { normalizeMode, resolveDisplayFlags } from "./display";

function parseBeforePlacement(value: string | undefined): LabelBeforePlacement | undefined {
  if (value === "left" || value === "top") {
    return value;
  }
  return undefined;
}

function parseAfterPlacement(value: string | undefined): LabelAfterPlacement | undefined {
  if (value === "right" || value === "bottom") {
    return value;
  }
  return undefined;
}

function readScriptTag(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement) {
    return current;
  }

  const tagged = document.querySelector("script[data-pandorium-footer]");
  return tagged instanceof HTMLScriptElement ? tagged : null;
}

function parsePlatform(value: string | null): FooterPlatform {
  if (value === "shopify" || value === "nuvemshop" || value === "generic") {
    return value;
  }
  return "generic";
}

function parseMode(value: string | null | undefined): FooterMode | undefined {
  if (!value) {
    return undefined;
  }
  if ((FOOTER_MODES as string[]).includes(value)) {
    return value as FooterMode;
  }
  if (value === "logo" || value === "text") {
    return value;
  }
  return undefined;
}

function parseAssets(dataset: DOMStringMap): FooterAssets | undefined {
  const assets: FooterAssets = {};

  if (dataset.logo) assets.logo = dataset.logo;
  if (dataset.logoLight) assets.logoLight = dataset.logoLight;
  if (dataset.logoDark) assets.logoDark = dataset.logoDark;
  if (dataset.icon) assets.icon = dataset.icon;
  if (dataset.avatar) assets.avatar = dataset.avatar;

  return Object.keys(assets).length > 0 ? assets : undefined;
}

function parseDisplayFlags(dataset: DOMStringMap) {
  const display: FooterConfig["display"] = {};

  if (dataset.showLogo === "true") display.showLogo = true;
  if (dataset.showLogo === "false") display.showLogo = false;
  if (dataset.showText === "true") display.showText = true;
  if (dataset.showText === "false") display.showText = false;
  if (dataset.showAvatar === "true") display.showAvatar = true;
  if (dataset.showAvatar === "false") display.showAvatar = false;
  if (dataset.showIcon === "true") display.showIcon = true;
  if (dataset.showIcon === "false") display.showIcon = false;

  return Object.keys(display).length > 0 ? display : undefined;
}

function parseDataset(script: HTMLScriptElement | null): Partial<FooterConfig> {
  if (!script) {
    return {};
  }

  const dataset = script.dataset;
  const partial: Partial<FooterConfig> = {};

  if (dataset.platform) {
    partial.platform = parsePlatform(dataset.platform);
  }
  if (dataset.clientId) {
    partial.clientId = dataset.clientId;
  }

  const mode = parseMode(dataset.mode ?? dataset.variant);
  if (mode) {
    partial.mode = mode;
    partial.variant = mode;
  }

  if (dataset.position === "inline" || dataset.position === "fixed") {
    partial.position = dataset.position;
  }
  const themeValue = dataset.contrast ?? dataset.theme;
  if (themeValue === "light" || themeValue === "dark" || themeValue === "auto") {
    partial.theme = themeValue;
  }
  if (dataset.layout === "inline" || dataset.layout === "stack") {
    partial.layout = dataset.layout;
  }
  const labelBeforePlacement = parseBeforePlacement(dataset.labelBeforePlacement);
  if (labelBeforePlacement) {
    partial.labelBeforePlacement = labelBeforePlacement;
  }
  const labelAfterPlacement = parseAfterPlacement(dataset.labelAfterPlacement);
  if (labelAfterPlacement) {
    partial.labelAfterPlacement = labelAfterPlacement;
  }
  if (dataset.labelAfter) {
    partial.labelAfter = dataset.labelAfter;
  }
  if (dataset.labelBefore) {
    partial.labelBefore = dataset.labelBefore;
  }
  if (dataset.link) {
    partial.link = dataset.link;
  }
  if (dataset.target) {
    partial.target = dataset.target;
  }
  if (dataset.separator) {
    partial.separator = dataset.separator;
  }
  if (dataset.insert === "append" || dataset.insert === "after" || dataset.insert === "before") {
    partial.insert = dataset.insert;
  }
  if (dataset.backgroundColor) {
    partial.backgroundColor = dataset.backgroundColor;
  }
  if (dataset.marginTop) {
    partial.marginTop = dataset.marginTop;
  }
  if (dataset.cdnBase) {
    partial.cdnBase = dataset.cdnBase;
  }
  if (dataset.assetsBase) {
    partial.assetsBase = dataset.assetsBase;
  }
  if (dataset.clientsBase) {
    partial.clientsBase = dataset.clientsBase;
  }
  if (dataset.enabled === "false") {
    partial.enabled = false;
  }

  const assets = parseAssets(dataset);
  if (assets) {
    partial.assets = assets;
  }

  const display = parseDisplayFlags(dataset);
  if (display) {
    partial.display = display;
  }

  return partial;
}

function mergeAssets(
  base?: FooterAssets,
  override?: FooterAssets
): FooterAssets | undefined {
  if (!base && !override) {
    return undefined;
  }
  return { ...base, ...override };
}

function mergeConfig(
  base: FooterConfig,
  override: Partial<FooterConfig>
): FooterConfig {
  const merged: FooterConfig = {
    ...base,
    ...override,
    link: override.link || base.link,
    labelAfter: override.labelAfter ?? base.labelAfter,
    labelBefore: override.labelBefore ?? base.labelBefore,
    labelBeforePlacement: override.labelBeforePlacement ?? base.labelBeforePlacement,
    labelAfterPlacement: override.labelAfterPlacement ?? base.labelAfterPlacement,
    separator: override.separator ?? base.separator,
    insert: override.insert ?? base.insert,
    backgroundColor: override.backgroundColor ?? base.backgroundColor,
    marginTop: override.marginTop ?? base.marginTop,
    target: override.target || base.target,
    cdnBase: override.cdnBase || base.cdnBase,
    assetsBase: override.assetsBase || base.assetsBase,
    clientsBase: override.clientsBase || base.clientsBase,
    assets: mergeAssets(base.assets, override.assets),
    display: { ...base.display, ...override.display },
  };

  merged.mode = normalizeMode({
    mode: override.mode ?? merged.mode,
    variant: override.variant ?? merged.variant,
  });

  return merged;
}

async function fetchClientConfig(
  clientsBase: string,
  clientId: string
): Promise<Partial<FooterConfig>> {
  try {
    const response = await fetch(`${clientsBase}/${clientId}.json`, {
      credentials: "omit",
    });
    if (!response.ok) {
      return {};
    }
    return (await response.json()) as Partial<FooterConfig>;
  } catch {
    return {};
  }
}

export async function resolveConfig(
  script?: HTMLScriptElement | null
): Promise<FooterConfig> {
  const scriptTag = script ?? readScriptTag();
  const datasetConfig = parseDataset(scriptTag);
  const platform = datasetConfig.platform ?? DEFAULT_CONFIG.platform;
  const platformPreset = PLATFORM_PRESETS[platform] ?? {};

  let config = mergeConfig(DEFAULT_CONFIG, platformPreset);
  config = mergeConfig(config, datasetConfig);

  let clientConfig: Partial<FooterConfig> = {};
  if (config.clientId) {
    clientConfig = await fetchClientConfig(
      config.clientsBase || DEFAULT_CLIENTS_BASE,
      config.clientId
    );
    config = mergeConfig(config, clientConfig);
  }

  config.link = config.link || DEFAULT_LINK;

  const afterInClient = "labelAfter" in clientConfig;
  const afterInDataset = "labelAfter" in datasetConfig;
  const afterExplicit = afterInClient || afterInDataset;
  const flags = resolveDisplayFlags(config);

  if (!afterExplicit && !flags.showText) {
    config.labelAfter = undefined;
  } else if (config.labelBefore && !afterExplicit) {
    config.labelAfter = undefined;
  } else if (
    normalizeMode(config) === "text" &&
    !config.labelBefore &&
    !config.labelAfter?.trim() &&
    flags.showText
  ) {
    config.labelAfter = DEFAULT_LABEL_AFTER;
  }
  config.cdnBase = config.cdnBase || DEFAULT_CDN_BASE;
  config.assetsBase = config.assetsBase || DEFAULT_ASSETS_BASE;
  config.clientsBase = config.clientsBase || DEFAULT_CLIENTS_BASE;
  config.mode = normalizeMode(config);

  return config;
}
